// src/components/dashboards/StudentEthicsSection.jsx
import React, { useState, useEffect } from 'react';
import { studentEthicsAPI, departmentAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { notify, confirmDialog } from '../../utils/notify';

const ACCENT = 'rgb(99, 102, 241)'; // Modern indigo color for Ethics
const ACCENT_L = 'rgba(99, 102, 241, 0.12)';

const labelStyle = {
  fontSize: '11px',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: '6px',
  display: 'block',
};

const inputStyle = {
  padding: '10px 14px',
  borderRadius: '8px',
  border: '1px solid #cbd5e1',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
  background: '#fff',
};

const btnStyle = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: 'none',
  background: ACCENT,
  color: '#fff',
  fontWeight: 700,
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background .15s',
};

const btnGhostStyle = {
  padding: '10px 20px',
  borderRadius: '8px',
  border: `1px solid ${ACCENT}`,
  background: '#fff',
  color: ACCENT,
  fontWeight: 700,
  fontSize: '13px',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const getFileIcon = (url) => {
  if (!url) return '📎';
  if (url.endsWith('.pdf')) return '📕';
  if (/\.(jpg|jpeg|png|gif|webp)/i.test(url)) return '🖼';
  return '📄';
};

const getStatusColor = (status) => {
  switch (status) {
    case 'DRAFT': return { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
    case 'SUBMITTED': return { bg: '#e0e7ff', color: '#4338ca', border: '#c7d2fe' };
    case 'UNDER_REVIEW': return { bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' };
    case 'CHANGES_REQUESTED': return { bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
    case 'APPROVED': return { bg: '#d1fae5', color: '#065f46', border: '#a7f3d0' };
    case 'REJECTED': return { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' };
    default: return { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' };
  }
};

const STUDY_TYPES = [
  { value: 'THESIS_STUDY', label: 'Thesis Study' },
  { value: 'RESEARCH_PUBLICATION', label: 'Research Publication' },
  { value: 'CASE_STUDY', label: 'Case Study' },
  { value: 'RESEARCH_PROJECT', label: 'Research Project' },
  { value: 'OTHER', label: 'Other' }
];

const StudentEthicsSection = () => {
  const { user } = useAuth();
  const isMBBS = user?.profile?.course === 'MBBS';
  const [submissions, setSubmissions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form Fields
  const [title, setTitle] = useState('');
  const [abstract, setAbstract] = useState('');
  const [studyType, setStudyType] = useState(isMBBS ? 'RESEARCH_PUBLICATION' : 'THESIS_STUDY');
  const [guideInputMode, setGuideInputMode] = useState('select'); // 'select' | 'text'
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [facultyList, setFacultyList] = useState([]);
  const [selectedFacultyId, setSelectedFacultyId] = useState('');
  const [guideName, setGuideName] = useState('');
  const [supportingFile, setSupportingFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Final PDF upload field
  const [finalPdf, setFinalPdf] = useState(null);
  const [uploadingFinal, setUploadingFinal] = useState(false);

  const hasApprovedThesisStudy = submissions.some(
    sub => sub.status === 'APPROVED' && sub.study_type === 'THESIS_STUDY'
  );

  const filteredStudyTypes = STUDY_TYPES.filter(t => {
    if (t.value === 'THESIS_STUDY') {
      if (isMBBS) {
        return false;
      }
      if (hasApprovedThesisStudy && studyType !== 'THESIS_STUDY') {
        return false;
      }
    }
    return true;
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch faculty when department changes
  useEffect(() => {
    if (selectedDeptId) {
      departmentAPI.getFacultyByDepartment(selectedDeptId)
        .then(data => setFacultyList(data))
        .catch(err => console.error('Failed to load faculty for department', err));
    } else {
      setFacultyList([]);
    }
  }, [selectedDeptId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsData, deptData] = await Promise.all([
        studentEthicsAPI.getSubmissions(),
        departmentAPI.getDepartments()
      ]);
      setSubmissions(subsData);
      setDepartments(deptData);
    } catch (err) {
      console.error('Failed to load ethics submissions', err);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${backendBase}${url}`;
  };

  const handleOpenForm = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setTitle(item.project_title || '');
      setAbstract(item.abstract || '');
      setStudyType(item.study_type || 'THESIS_STUDY');
      
      if (item.faculty) {
        setGuideInputMode('select');
        setSelectedFacultyId(item.faculty);
        // Find department of faculty to pre-select department if possible
        setSelectedDeptId('');
      } else {
        setGuideInputMode('text');
        setGuideName(item.guide_name || '');
        setSelectedFacultyId('');
        setSelectedDeptId('');
      }
      
      setSupportingFile(null);
      setIsEditing(true);
    } else {
      setTitle('');
      setAbstract('');
      setStudyType((isMBBS || hasApprovedThesisStudy) ? 'RESEARCH_PUBLICATION' : 'THESIS_STUDY');
      setGuideInputMode('select');
      setSelectedDeptId('');
      setFacultyList([]);
      setSelectedFacultyId('');
      setGuideName('');
      setSupportingFile(null);
      setIsEditing(false);
    }
    setShowForm(true);
  };

  const handleFormSubmit = async (e, submitAsDraft = false) => {
    if (e) e.preventDefault();
    if (!title.trim() || !abstract.trim()) {
      notify.error('Please fill in all required fields (Project Title, Abstract).');
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('project_title', title.trim());
    formData.append('abstract', abstract.trim());
    formData.append('study_type', studyType);
    formData.append('status', submitAsDraft ? 'DRAFT' : 'SUBMITTED');

    if (guideInputMode === 'select') {
      if (selectedFacultyId) {
        formData.append('faculty', selectedFacultyId);
      }
      formData.append('guide_name', '');
    } else {
      formData.append('faculty', '');
      formData.append('guide_name', guideName.trim());
    }

    if (supportingFile) {
      formData.append('reference_document', supportingFile);
    }

    try {
      let updated;
      if (isEditing && selectedItem) {
        updated = await studentEthicsAPI.updateEthics(selectedItem.id, formData);
        notify.success(submitAsDraft ? 'Ethics proposal saved as draft!' : 'Ethics proposal submitted for review!');
      } else {
        updated = await studentEthicsAPI.submitEthics(formData);
        notify.success(submitAsDraft ? 'Ethics proposal saved as draft!' : 'Ethics proposal submitted for review!');
      }
      setShowForm(false);
      await fetchData();
      if (selectedItem) {
        const freshDetail = await studentEthicsAPI.getEthicsDetail(selectedItem.id);
        setSelectedItem(freshDetail);
      }
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to submit proposal.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadFinalPdf = async (e) => {
    e.preventDefault();
    if (!finalPdf) {
      notify.error('Please select a PDF file first.');
      return;
    }
    setUploadingFinal(true);
    const formData = new FormData();
    formData.append('file', finalPdf);
    try {
      const data = await studentEthicsAPI.uploadFinalPdf(selectedItem.id, formData);
      notify.success('Final PDF document uploaded successfully!');
      setFinalPdf(null);
      const freshDetail = await studentEthicsAPI.getEthicsDetail(selectedItem.id);
      setSelectedItem(freshDetail);
      await fetchData();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to upload final document.');
    } finally {
      setUploadingFinal(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>Loading Ethics Submissions…</div>;
  }

  if (showForm) {
    return (
      <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
            {isEditing ? 'Edit Ethics Proposal' : 'New Ethics Proposal Submission'}
          </h3>
          <button 
            type="button" 
            onClick={() => setShowForm(false)} 
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#94a3b8' }}
          >
            ×
          </button>
        </div>

        <form onSubmit={(e) => handleFormSubmit(e, false)} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Study Title *</label>
            <input 
              style={inputStyle} 
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              placeholder="e.g. Comparative Study on Ethics in Patient Care" 
              required 
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <label style={labelStyle}>Study Type *</label>
              <select
                style={inputStyle}
                value={studyType}
                onChange={e => setStudyType(e.target.value)}
              >
                {filteredStudyTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Guide Selection Mode</label>
              <div style={{ display: 'flex', gap: '12px', height: '42px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="guideInputMode"
                    value="select"
                    checked={guideInputMode === 'select'}
                    onChange={() => setGuideInputMode('select')}
                  />
                  Search Faculty List
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="guideInputMode"
                    value="text"
                    checked={guideInputMode === 'text'}
                    onChange={() => setGuideInputMode('text')}
                  />
                  Enter Guide Name (Text)
                </label>
              </div>
            </div>
          </div>

          {guideInputMode === 'select' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <div>
                <label style={labelStyle}>Guide Department</label>
                <select
                  style={inputStyle}
                  value={selectedDeptId}
                  onChange={e => setSelectedDeptId(e.target.value)}
                >
                  <option value="">-- Select Department --</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Faculty Guide</label>
                <select
                  style={inputStyle}
                  value={selectedFacultyId}
                  onChange={e => setSelectedFacultyId(e.target.value)}
                  disabled={!selectedDeptId}
                >
                  <option value="">-- Select Faculty --</option>
                  {facultyList.map(f => (
                    <option key={f.id} value={f.id}>{f.first_name} {f.last_name} ({f.username})</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <label style={labelStyle}>Guide Name (Text Fallback)</label>
              <input 
                style={inputStyle} 
                value={guideName} 
                onChange={e => setGuideName(e.target.value)} 
                placeholder="Enter complete Guide name and designation" 
              />
            </div>
          )}

          <div>
            <label style={labelStyle}>Abstract / Summary *</label>
            <textarea 
              style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }} 
              value={abstract} 
              onChange={e => setAbstract(e.target.value)} 
              placeholder="Provide clinical details, objectives, and ethical questions involved..." 
              required 
            />
          </div>

          <div>
            <label style={labelStyle}>Supporting / Reference Document (Optional Upload)</label>
            <input 
              type="file" 
              accept=".pdf,.doc,.docx" 
              onChange={e => setSupportingFile(e.target.files[0])} 
              style={{ fontSize: '13px' }}
            />
            {isEditing && selectedItem?.reference_document && (
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                Current file: <a href={getFullUrl(selectedItem.reference_document)} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, textDecoration: 'none', fontWeight: 600 }}>View Document</a>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
            <button 
              type="button" 
              onClick={() => setShowForm(false)} 
              style={{ ...btnGhostStyle, borderColor: '#cbd5e1', color: '#64748b' }}
            >
              Cancel
            </button>
            <button 
              type="button" 
              disabled={submitting} 
              onClick={() => handleFormSubmit(null, true)}
              style={btnGhostStyle}
            >
              {submitting ? 'Saving...' : '💾 Save Draft'}
            </button>
            <button type="submit" disabled={submitting} style={btnStyle}>
              {submitting ? 'Submitting...' : '🚀 Submit Proposal'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", display: 'grid', gridTemplateColumns: selectedItem ? '1fr 1fr' : '1fr', gap: '20px' }}>
      
      {/* Left Column: Submissions List */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
              My Ethics Clearance Proposals
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
              You may submit multiple research studies. Ethics Clearance is the starting point for PG research.
            </p>
          </div>
          <button onClick={() => handleOpenForm()} style={btnStyle}>
            ➕ New Study Proposal
          </button>
        </div>

        {submissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '2px dashed #cbd5e1' }}>
            No research studies submitted yet. Click "New Study Proposal" above to get started.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {submissions.map(item => {
              const isSelected = selectedItem?.id === item.id;
              const statusColor = getStatusColor(item.status);
              const studyTypeLabel = STUDY_TYPES.find(t => t.value === item.study_type)?.label || item.study_type;
              
              return (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  style={{ 
                    padding: '14px 16px', 
                    borderRadius: '10px', 
                    border: `1px solid ${isSelected ? ACCENT : '#e2e8f0'}`,
                    background: isSelected ? ACCENT_L : '#f8fafc',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '70%' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: ACCENT }}>{studyTypeLabel}</span>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', lineHeight: 1.3 }}>
                        {item.project_title}
                      </div>
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: statusColor.bg,
                      color: statusColor.color,
                      border: `1px solid ${statusColor.border}`
                    }}>
                      {item.status_display || item.status}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 11, color: '#64748b', borderTop: '1px dashed #e2e8f0', paddingTop: '8px' }}>
                    <span>Guide: {item.guide_name_display || 'N/A'}</span>
                    <span>Submitted: {new Date(item.submitted_at).toLocaleDateString('en-IN')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Right Column: Selected Submission Detail & Actions */}
      {selectedItem && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
              Study Proposal Details
            </h3>
            <button 
              onClick={() => setSelectedItem(null)} 
              style={{ background: 'none', border: 'none', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}
            >
              Close Details ×
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={labelStyle}>Study Type</span>
                <div style={{ fontSize: 14, fontWeight: 700, color: ACCENT }}>
                  {STUDY_TYPES.find(t => t.value === selectedItem.study_type)?.label || selectedItem.study_type}
                </div>
              </div>
              <div>
                <span style={labelStyle}>Status</span>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 700,
                  background: getStatusColor(selectedItem.status).bg,
                  color: getStatusColor(selectedItem.status).color,
                  border: `1px solid ${getStatusColor(selectedItem.status).border}`,
                  display: 'inline-block'
                }}>
                  {selectedItem.status_display || selectedItem.status}
                </span>
              </div>
            </div>

            <div>
              <span style={labelStyle}>Title</span>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{selectedItem.project_title}</div>
            </div>

            <div>
              <span style={labelStyle}>Abstract / Summary</span>
              <div style={{ fontSize: 12, color: '#334155', background: '#f8fafc', padding: '10px', borderRadius: 8, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {selectedItem.abstract}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <span style={labelStyle}>Student Reg No</span>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedItem.student_id_num || 'N/A'}</div>
              </div>
              <div>
                <span style={labelStyle}>Guide Name</span>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedItem.guide_name_display || 'N/A'}</div>
              </div>
            </div>

            {selectedItem.reference_document && (
              <div>
                <span style={labelStyle}>Supporting Reference Document</span>
                <a 
                  href={getFullUrl(selectedItem.reference_document)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: ACCENT, textDecoration: 'none', fontWeight: 600 }}
                >
                  {getFileIcon(selectedItem.reference_document)} View Reference Document
                </a>
              </div>
            )}

            {/* Status alerts & review remarks */}
            {(selectedItem.status === 'CHANGES_REQUESTED' || selectedItem.status === 'REJECTED') && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px' }}>
                <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 12 }}>Reviewer Remarks:</div>
                <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 4 }}>
                  {selectedItem.remarks || 'No remarks provided.'}
                </div>
                <button 
                  onClick={() => handleOpenForm(selectedItem)} 
                  style={{ ...btnStyle, background: '#ef4444', marginTop: '10px', width: '100%' }}
                >
                  ✏️ Edit & Re-submit Proposal
                </button>
              </div>
            )}

            {selectedItem.status === 'DRAFT' && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '12px', color: '#475569' }}>
                  ℹ️ This is a saved draft. It has not been submitted to the Faculty yet.
                </div>
                <button 
                  onClick={() => handleOpenForm(selectedItem)} 
                  style={{ ...btnStyle, width: '100%' }}
                >
                  ✏️ Edit & Submit Draft
                </button>
              </div>
            )}

            {selectedItem.status === 'SUBMITTED' && (
              <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 10, padding: '12px', color: '#b45309', fontSize: '12px' }}>
                ℹ️ This proposal is currently pending review by the Community Medicine Faculty.
              </div>
            )}

            {/* APPROVED: Gated final PDF upload */}
            {selectedItem.status === 'APPROVED' && (
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '12px', color: '#166534', fontSize: '12px', fontWeight: 600 }}>
                  🎉 Ethics Proposal Approved!
                </div>

                {selectedItem.study_type !== 'THESIS_STUDY' && (
                  <div>
                    <span style={labelStyle}>Final Approved Ethics Project Document</span>
                    {selectedItem.final_document ? (
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 18 }}>📕</span>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>Final_Ethics_Report.pdf</span>
                        </div>
                        <a 
                          href={getFullUrl(selectedItem.final_document.file_url || selectedItem.final_document.file)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '12px', color: ACCENT, textDecoration: 'none', fontWeight: 700 }}
                        >
                          Open
                        </a>
                      </div>
                    ) : (
                      <form onSubmit={handleUploadFinalPdf} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                          Please upload the final signed/approved Ethics Project PDF document for grading by the COE.
                        </p>
                        <input 
                          type="file" 
                          accept=".pdf" 
                          required 
                          onChange={e => setFinalPdf(e.target.files[0])}
                          style={{ fontSize: '12px' }}
                        />
                        <button 
                          type="submit" 
                          disabled={uploadingFinal || !finalPdf}
                          style={{ ...btnStyle, padding: '8px 14px', alignSelf: 'flex-start' }}
                        >
                          {uploadingFinal ? 'Uploading...' : '⬆️ Upload Final PDF'}
                        </button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentEthicsSection;
